import { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { MessageCircle, Mail, Phone } from "lucide-react";
import { trackContact } from "@/lib/trackContact";

export default function Contact() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      // Fire Contact / generate_lead ONLY on successful submission so we don't inflate
      // the lead count with validation errors or network failures.
      trackContact("form", "contact_page");
      toast.success(t("contact_success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("contact_error"));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={t("contact_seo_title")}
        description={t("contact_seo_desc")}
        path="/contatti"
        lang={lang}
      />
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{t("contact_kicker")}</div>
          <h1 className="font-display text-4xl font-semibold mt-2">{t("contact_h1")}</h1>
          <p className="mt-3 text-slate-300 max-w-xl">{t("contact_intro")}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-[1fr_280px] gap-8">
        <form onSubmit={submit} className="bg-white border border-xd-border rounded-xl p-6 space-y-4">
          <Input placeholder={t("contact_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
          <Input type="email" placeholder={t("contact_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
          <Textarea rows={5} placeholder={t("contact_message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" />
          <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-cta rounded-lg px-6 py-3 text-sm">
            {sending ? t("contact_sending") : t("contact_send")}
          </button>
        </form>
        <aside className="space-y-3">
          <a href="https://wa.me/393511294525" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" onClick={() => trackContact("whatsapp", "contact_page")} className="flex items-center gap-3 bg-white border border-xd-border rounded-xl p-4 hover:border-xd-success transition-colors">
            <MessageCircle className="w-5 h-5 text-xd-success" />
            <div>
              <div className="font-semibold text-sm">WhatsApp</div>
              <div className="text-xs text-xd-muted">+39 351 129 4525</div>
            </div>
          </a>
          <a href="mailto:support@audrivio.com" data-testid="contact-email-link" onClick={() => trackContact("email", "contact_page")} className="flex items-center gap-3 bg-white border border-xd-border rounded-xl p-4 hover:border-xd-cyan transition-colors">
            <Mail className="w-5 h-5 text-xd-cyanHover" />
            <div>
              <div className="font-semibold text-sm">Email</div>
              <div className="text-xs text-xd-muted">support@audrivio.com</div>
            </div>
          </a>
          <div className="flex items-center gap-3 bg-white border border-xd-border rounded-xl p-4">
            <Phone className="w-5 h-5 text-xd-text" />
            <div>
              <div className="font-semibold text-sm">{t("contact_support")}</div>
              <div className="text-xs text-xd-muted">{t("contact_support_hours")}</div>
            </div>
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
